<script setup lang="ts">
  import { ref, reactive, computed, onMounted, watch, type Ref } from 'vue';

  import {
    UiModalHeader,
    SelectInput,
    IconRadio,
    ColoredButton,
    ThemeProvider,
    DatePicker,
    IconImage,
  } from '@/shared/ui';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import EditDocumentValidity from './EditDocumentValidity.vue';
  import EditDocumentFormat from './EditDocumentFormat.vue';
  import useDocumentEditModal from '../model/useDocumentEditModal';
  import type { FormSnapshot } from '../model/useDocumentEditModal.types';
  import { parseCivilDate } from '@/shared/lib';

  import type { DocumentRevision } from '@/entities/Document';
  import { useUserStore } from '@/entities/User';

  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';

  const startOfToday = (): Date => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const props = defineProps<{
    title: string;
    revision: DocumentRevision | null;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'saved'): void;
  }>();

  const handleClose = () => {
    emit('close');
  };

  const prefix = computed(() => props.revision?.document?.prefix ?? '');
  const notificationMembers = computed(() => props.revision?.notifications?.members ?? []);
  const revisionNumber = computed(() => String(props.revision?.revision ?? 0).padStart(2, '0'));

  const userStore = useUserStore();
  const collaboratorDisplayName = computed(() => userStore.name ?? 'Usuário atual');

  const initialFormData = ref<FormSnapshot | null>(null);

  const initialHtmRows =
    props.revision?.physicalLocations && props.revision.physicalLocations.length > 0
      ? props.revision.physicalLocations.map((location) => ({
          local: String(location.id),
          copies: location.distribution?.quantity ?? 0,
        }))
      : [{ local: String(props.revision?.physicalLocations?.[0]?.id ?? ''), copies: 0 }];

  const initialStatus = computed(() => {
    if (props.revision?.status === 'CURRENT') return 'vigente';
    if (props.revision?.status === 'INACTIVE') return 'inativo';
    return '';
  });

  const formData = reactive({
    name: props.revision?.document?.name ?? '',
    hasValidity: props.revision?.validityDate ? 'true' : 'false',
    format: props.revision?.config?.format ?? 'EL',
    status: initialStatus.value,
    includeInMasterList: String(props.revision?.config?.includeInMasterList ?? true),
    positionId: String(props.revision?.config?.editedByPosition?.id ?? ''),
    collaboratorId: String(userStore.memberId ?? ''),
    configurationDate: startOfToday() as Date | null,
    validityDate: parseCivilDate(props.revision?.validityDate ?? undefined),
    notificationDates: (props.revision?.notifications?.dates ?? [])
      .map((d) => parseCivilDate(d))
      .filter((d): d is Date => d !== null),
    notificationMemberIds:
      props.revision?.notifications?.members?.map((member) => member.id) ?? ([] as number[]),
    htmRows: initialHtmRows as { local: string; copies: number }[],
  });

  watch(
    () => userStore.memberId,
    (newMemberId) => {
      formData.collaboratorId = String(newMemberId ?? '');
    },
    { immediate: true }
  );

  watch(
    () => props.revision?.id,
    (revisionId) => {
      if (revisionId == null) return;
      const today = startOfToday();
      formData.configurationDate = today;
      if (initialFormData.value) {
        initialFormData.value.configurationDate = new Date(today);
      }
    }
  );

  const notifiedUsersCount = ref(0);

  const handleNotifiedMemberIds = (ids: number[]) => {
    formData.notificationMemberIds = ids;
  };

  const notificationUnchanged = computed(() => {
    if (!initialFormData.value) return false;

    return (
      JSON.stringify(formData.notificationDates) ===
        JSON.stringify(initialFormData.value.notificationDates) &&
      JSON.stringify(formData.notificationMemberIds) ===
        JSON.stringify(initialFormData.value.notificationMemberIds)
    );
  });

  const {
    positionOptions,
    positionsResponse,
    formatDisplayDate,
    handleSave,
    canSave,
    openDatePicker,
    isDatePickerOpen,
  } = useDocumentEditModal(
    props,
    emit,
    formData,
    notifiedUsersCount as Ref<number>,
    notificationUnchanged
  );

  const toDateTimestamp = (value: Date | string | null) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
  };

  const hasFormChanges = computed(() => {
    if (!initialFormData.value) return false;

    return (
      formData.name !== initialFormData.value.name ||
      formData.hasValidity !== initialFormData.value.hasValidity ||
      formData.format !== initialFormData.value.format ||
      formData.status !== initialFormData.value.status ||
      formData.includeInMasterList !== initialFormData.value.includeInMasterList ||
      formData.collaboratorId !== initialFormData.value.collaboratorId ||
      formData.positionId !== initialFormData.value.positionId ||
      toDateTimestamp(formData.configurationDate) !==
        toDateTimestamp(initialFormData.value.configurationDate) ||
      toDateTimestamp(formData.validityDate) !==
        toDateTimestamp(initialFormData.value.validityDate) ||
      !notificationUnchanged.value ||
      JSON.stringify(formData.htmRows) !== JSON.stringify(initialFormData.value.htmRows)
    );
  });

  onMounted(() => {
    initialFormData.value = {
      name: formData.name,
      hasValidity: formData.hasValidity,
      format: formData.format,
      status: formData.status,
      includeInMasterList: formData.includeInMasterList,
      collaboratorId: formData.collaboratorId,
      positionId: formData.positionId,
      configurationDate: formData.configurationDate ? new Date(formData.configurationDate) : null,
      validityDate: formData.validityDate,
      notificationDates: [...formData.notificationDates],
      notificationMemberIds: [...formData.notificationMemberIds],
      htmRows: JSON.parse(JSON.stringify(formData.htmRows)),
    };
  });
</script>

<template>
  <div class="flex flex-col bg-white-01 rounded-2xl w-[85rem] max-h-[90vh] overflow-hidden">
    <ThemeProvider class="flex flex-col min-h-0">
      <UiModalHeader
        class="shrink-0 px-6 py-4 text-sm border border-gray-02"
        :title="props.title"
        @close="handleClose"
      />
      <div
        id="modal-content"
        class="flex flex-col flex-1 min-h-0 overflow-auto"
      >
        <div class="flex flex-col divide-y divide-gray-02">
          <!-- Prefixo + Revisão (disabled) -->
          <div class="flex flex-row gap-4 p-6">
            <div class="flex flex-col gap-2 flex-1">
              <label class="text-sm font-semibold text-gray-07">Prefixo:</label>
              <input
                type="text"
                :value="prefix"
                disabled
                class="w-full p-3 border border-gray-02 rounded-md text-gray-07 bg-gray-50 cursor-not-allowed"
              >
            </div>
            <div class="flex flex-col gap-2 w-28">
              <label class="text-sm font-semibold text-gray-07">Revisão:</label>
              <input
                type="text"
                :value="revisionNumber"
                disabled
                class="w-full p-3 border border-gray-02 rounded-md text-gray-07 bg-gray-50 cursor-not-allowed"
              >
            </div>
          </div>

          <!-- Nome do Documento -->
          <div class="flex flex-col gap-2 p-6">
            <label
              for="name"
              class="text-sm font-semibold text-gray-07"
            >
              Nome do Documento:
            </label>
            <input
              id="name"
              v-model="formData.name"
              type="text"
              class="w-full p-3 border border-gray-02 rounded-md text-gray-07 focus:outline-none focus:border-gray-07"
            >
          </div>

          <!-- Documento tem validade? -->
          <EditDocumentValidity
            :id="props.revision?.id"
            v-model:has-validity="formData.hasValidity"
            v-model:validity-date="formData.validityDate"
            v-model:notification-dates="formData.notificationDates"
            :positions-response="positionsResponse"
            :notification-members="notificationMembers"
            :validate-from-today="true"
            @update:notified-users-count="notifiedUsersCount = $event"
            @update:notified-member-ids="handleNotifiedMemberIds"
          />

          <!-- Formato -->
          <EditDocumentFormat
            v-model:format="formData.format"
            v-model:rows="formData.htmRows"
          />

          <!-- Status -->
          <div class="flex flex-col gap-3 p-6">
            <label class="text-sm font-semibold text-gray-07">Status:</label>
            <div class="flex gap-6">
              <IconRadio
                v-model="formData.status"
                value="vigente"
                label="Vigente"
                name="status"
              />
              <IconRadio
                v-model="formData.status"
                value="inativo"
                label="Inativo"
                name="status"
              />
            </div>
          </div>

          <div class="flex flex-col gap-3 p-6">
            <label class="text-sm font-semibold text-gray-07">Incluir na Lista Mestra:</label>
            <div class="flex gap-6">
              <IconRadio
                v-model="formData.includeInMasterList"
                value="true"
                label="Sim"
                name="includeInMasterList"
              />
              <IconRadio
                v-model="formData.includeInMasterList"
                value="false"
                label="Não"
                name="includeInMasterList"
              />
            </div>
          </div>

          <!-- Colaborador / Cargo / Data da configuração -->
          <div class="grid grid-cols-3 gap-4 p-6">
            <div class="flex text-sm flex-col gap-2">
              <label class="font-semibold text-gray-07">Colaborador:</label>
              <input
                :value="collaboratorDisplayName"
                type="text"
                disabled
                class="w-full p-3 border border-gray-02 rounded-md text-gray-07 bg-gray-50 cursor-not-allowed"
              >
            </div>

            <div class="flex text-sm flex-col gap-2">
              <label class="font-semibold text-gray-07">Cargo:</label>
              <SelectInput
                v-model="formData.positionId"
                :options="positionOptions"
                label="Selecione o cargo"
              />
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-semibold text-gray-07">Data da configuração:</label>
              <button
                type="button"
                class="flex items-center gap-2 w-2/3 p-3 border border-gray-02 rounded-lg text-gray-07 focus:outline-none focus:border-gray-07 bg-white-01"
                @click="openDatePicker"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendar"
                />
                <span class="text-left text-sm">{{
                  formatDisplayDate(formData.configurationDate ?? null)
                }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="shrink-0 flex justify-end gap-3 p-6 border-t border-gray-02">
        <ColoredButton
          text="Cancelar"
          outlined
          @click="handleClose"
        />
        <ColoredButton
          text="Salvar"
          :icon="CheckMarkIcon"
          :disabled="!hasFormChanges || !canSave"
          @click="handleSave"
        />
      </div>
      <DatePicker
        v-model:is-open="isDatePickerOpen"
        :selected-date="formData.configurationDate ?? null"
        :disable-future-dates="true"
        @update:selected-date="(date: Date | null) => (formData.configurationDate = date)"
      />
    </ThemeProvider>
  </div>
</template>
